import {
  collection,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  FieldValue,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { Invitation, InvitationStatus } from '../types/invitation';

class InvitationService {
  private readonly collectionName = 'invitations';

  async getInvitations(email: string): Promise<Invitation[]> {
    let invitationsQuery = query(
      collection(db, this.collectionName),
      where('athleteEmail', '==', email),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(invitationsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      expiresAt: doc.data().expiresAt?.toDate() || new Date(),
      acceptedAt: doc.data().acceptedAt?.toDate() || new Date(),
      declinedAt: doc.data().declinedAt?.toDate() || new Date(),
    })) as Invitation[];
  }

  async getInvitationsByCoach(coachId: string): Promise<Invitation[]> {
    let invitationsQuery = query(
      collection(db, this.collectionName),
      where('coachId', '==', coachId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(invitationsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      expiresAt: doc.data().expiresAt?.toDate(),
      acceptedAt: doc.data().acceptedAt?.toDate(),
      declinedAt: doc.data().declinedAt?.toDate(),
    })) as Invitation[];
  }

  /** Accepting creates the organisation membership on the server; declining is a direct status write. */
  async updateInvitationStatus(invitationId: string, status: InvitationStatus): Promise<void> {
    try {
      if (status === 'accepted') {
        const acceptInvitation = httpsCallable(functions, 'acceptInvitation');
        await acceptInvitation({ invitationId });
        return;
      }

      const updateData: { status: InvitationStatus; updatedAt: FieldValue; declinedAt?: FieldValue } = {
        status,
        updatedAt: serverTimestamp(),
      };
      if (status === 'declined') {
        updateData.declinedAt = serverTimestamp();
      }
      await updateDoc(doc(db, this.collectionName, invitationId), updateData);
    } catch (error) {
      console.error('Error updating invitation status:', error);
      throw error;
    }
  }
}

export const invitationService = new InvitationService();
